import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MatDialog } from '@angular/material/dialog'
import { TranslateService } from '@ngx-translate/core'
import { firstValueFrom, Subscription } from 'rxjs'
import moment from 'moment'
import { AreYouSureModalComponent } from 'src/app/components/complex/modals/are-you-sure-modal/are-you-sure-modal.component'
import { ClientInformationAttribute } from 'src/app/models/client-information/ClientInformationAttribute'
import { ClientDto } from 'src/app/models/client/client-dto'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import {
  getStepDefinition,
  ServicePlanStepDefinition,
} from 'src/app/models/service-plan/service-plan-step-definition'
import { ServicePlanStepId } from 'src/app/models/service-plan/service-plan-step-id'
import { getVisibleSteps } from 'src/app/models/service-plan/service-plan-visibility'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { CommonService } from 'src/app/services/common/common.service'
import { NdisPlanService } from 'src/app/services/ndis-plan/ndis-plan.service'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'
import { ServicePlanStepAnnouncerService } from 'src/app/services/service-plan/service-plan-step-announcer.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard'

// The Guided Service Plan Stepper shell (story e8-1, generalised by e8-2 Task 8). Owns the step
// rail, the current-step content host, the shared error summary / accessibility scaffolding, and
// Continue/Back navigation. 'planType' (Step 1), 'ndisPlan' (Step 2), 'ndisClassification'
// (Step 3), 'baselinePackage' (Step 4), 'familyGoals' (Step 5), and 'hearingLanguageSupport'
// (Step 6) have real content — every other step id the engine can reach still renders
// StepPlaceholderComponent (e8-1 AC9).
@Component({
  selector: 'app-service-plan-stepper',
  templateUrl: './service-plan-stepper.component.html',
  styleUrls: ['./service-plan-stepper.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicePlanStepperComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  clientId = ''
  clientDetails?: ClientDto
  age = ''
  dob?: Date
  clientInformationAttributes: ClientInformationAttribute[] =
    this.commonService.createEmptyAttributeList()

  visibleSteps: ServicePlanStepDefinition[] = []
  currentStepId: ServicePlanStepId = 'planType'
  maxReachedIndex = 0
  errors: ServicePlanFieldError[] = []

  // Page chrome parity with every other client-scoped tool screen (FLI-P/REDDI/SLAT) —
  // Client Information card + app-sub-menu above the tool's own content. Reuses the
  // exact CommonService helpers client-dashboard/flip-assessment etc. already use for
  // this, not a new access-check mechanism.
  _hasAccess: any = {
    flipHasAccess: false,
    reddiHasAccess: false,
    slatHasAccess: false,
  }
  _totalReviews = 0

  // Generalised step-component wiring (story e8-2 Task 8 — see the story's "Critical correction"):
  // both <app-service-plan-plan-type-step> and <app-service-plan-ndis-plan-step> carry the same
  // #activeStep template-ref, each behind its own mutually-exclusive *ngIf on currentStepId, so
  // exactly one is ever rendered and this single ViewChild always picks up whichever one it is.
  @ViewChild('activeStep') activeStep?: ServicePlanStepComponent
  @ViewChild('stepHeading') stepHeading?: ElementRef<HTMLElement>

  private paramMapSub?: Subscription
  private payloadSub?: Subscription
  private ndisNumberSub?: Subscription
  private focusTimeoutId?: ReturnType<typeof setTimeout>
  private clientSwitchInFlight = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private clientService: ClientService,
    public commonService: CommonService,
    private bannerService: BannerService,
    private spinnerService: SpinnerService,
    private state: ServicePlanStateService,
    public announcer: ServicePlanStepAnnouncerService,
    private ndisPlanService: NdisPlanService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.paramMapSub = this.route.paramMap.subscribe((paramMap) => {
      const newClientId = paramMap.get('clientId') ?? ''

      if (!this.commonService.isValidUUID(newClientId)) {
        this.bannerService.showBanner(
          `Invalid clientId ${newClientId}.`,
          'error',
          30000,
        )
        return
      }

      // Client-in-focus binding (FR5): a same-route clientId change must never
      // silently continue with in-memory answers bound to the previous client.
      // Angular's default RouteReuseStrategy reuses this same component instance
      // for a param-only change on the same route, so we force a clean restart by
      // navigating away and back rather than attempting a partial in-place reset.
      if (this.clientId && newClientId !== this.clientId) {
        if (this.clientSwitchInFlight) {
          return
        }
        this.clientSwitchInFlight = true
        this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then((navigated) => {
            this.clientSwitchInFlight = false
            // navigateByUrl resolves false when this route's own canDeactivate
            // guard blocks it (e.g. the clinician chose "No, continue editing" on
            // the unsaved-changes dialog) — honour that choice instead of
            // switching clients anyway.
            if (navigated) {
              this.router.navigate(['/service-plan', newClientId])
            }
          })
        return
      }

      if (this.clientId) {
        return
      }

      this.clientId = newClientId
      this.state.reset(this.clientId)
      this.loadClient()
    })
  }

  ngOnDestroy(): void {
    this.paramMapSub?.unsubscribe()
    this.payloadSub?.unsubscribe()
    this.ndisNumberSub?.unsubscribe()
    clearTimeout(this.focusTimeoutId)
    // This service is a `providedIn: 'root'` singleton, so it outlives this
    // component — clear the bound client's in-memory answers on the way out
    // rather than leaving clinical PII resident until the next time someone
    // enters the route (ARCH3 / CLAUDE.md privacy-by-design).
    this.state.clear()
  }

  canDeactivate(): Promise<boolean> {
    // Story e9-2 (AC7) — a submission that already landed safely in Dataverse must never trigger
    // the "unsaved changes" dialog, even though hasAnswers() stays true (the payload is never
    // cleared mid-session after a successful Send).
    if (!this.state.hasAnswers() || this.state.hasActiveSubmission()) {
      return Promise.resolve(true)
    }
    return this.showAreYouSureDialog()
  }

  get isContinueDisabled(): boolean {
    // Fail closed when no step is mounted yet (e.g. before the ViewChild resolves) — only once a
    // step is actually rendered does its own isLiveValid (optional on the contract) get to decide.
    // A step that omits it (e.g. NdisPlanStepComponent, which has no required fields) is never
    // disabled by default, per DESIGN.md's "Continue must reflect live validity" note and Task 8.
    if (!this.activeStep) {
      return true
    }
    return this.activeStep.isLiveValid === false
  }

  onContinue(): void {
    // No real step component/validation exists for a placeholder step (AC9) — the
    // Continue button is already hidden for these (`*ngIf="!isPlaceholderStep"`),
    // but guard here too so this method is never a silent no-validation advance
    // path if that template condition ever changes independently.
    if (this.isPlaceholderStep) {
      return
    }

    const valid = this.activeStep?.isValid() ?? false
    if (!valid) {
      this.errors = this.activeStep?.getValidationErrors() ?? []
      this.cd.markForCheck()
      return
    }

    this.errors = []
    const currentIndex = this.visibleSteps.findIndex(
      (step) => step.id === this.currentStepId,
    )
    const nextStep = this.visibleSteps[currentIndex + 1]
    if (!nextStep) {
      return
    }

    this.maxReachedIndex = Math.max(this.maxReachedIndex, currentIndex + 1)
    this.goToStep(nextStep.id)
  }

  onBack(): void {
    const currentIndex = this.visibleSteps.findIndex(
      (step) => step.id === this.currentStepId,
    )

    if (currentIndex <= 0) {
      this.goToDashboard()
      return
    }

    this.goToStep(this.visibleSteps[currentIndex - 1].id)
  }

  // Shared destination for the sub-menu's own Back link and Step 1's in-form Back
  // button. Deliberately does NOT pre-check canDeactivate()/open the confirm dialog
  // itself — the route's own `canDeactivate: [CanDeactivateGuard]` (app-routing.module.ts)
  // already calls this component's canDeactivate() for every navigation away from this
  // route, from whichever UI element triggered it. Checking it here too would show the
  // same confirmation dialog twice.
  goToDashboard(): void {
    this.router.navigate(['/client-dashboard', this.clientId])
  }

  onStepSelected(stepId: ServicePlanStepId): void {
    this.errors = []
    this.goToStep(stepId)
  }

  // Steps with real content so far — every other reachable step id still renders
  // StepPlaceholderComponent (e8-1 AC9). Extend this list as each e8-3+ story ships its step.
  private static readonly stepsWithRealContent: ServicePlanStepId[] = [
    'planType',
    'ndisPlan',
    'ndisClassification',
    'baselinePackage',
    'familyGoals',
    'hearingLanguageSupport',
    'additionalServices',
    'cfss',
    'audiology',
    'continuityPlanning',
    'review',
  ]

  get isPlaceholderStep(): boolean {
    return !ServicePlanStepperComponent.stepsWithRealContent.includes(
      this.currentStepId,
    )
  }

  get currentStepTitleKey(): string {
    return getStepDefinition(this.currentStepId).titleKey
  }

  get currentStepNumber(): number {
    return (
      this.visibleSteps.findIndex((step) => step.id === this.currentStepId) + 1
    )
  }

  // Story e8-6 — parses the leading integer years out of `this.age`'s
  // "{N}y {M}m" format (AgeService.CalculateAgeString), the same string already
  // shown in the Client Information card, so the age-4+ rule and the on-screen
  // age can never disagree. Returns null (age unknown) when unparseable/absent —
  // AdditionalServicesStepComponent's age rule fails open in that case.
  get clientAgeYears(): number | null {
    const match = /^(\d+)y/.exec(this.age)
    return match ? Number(match[1]) : null
  }

  private loadClient(): void {
    this.spinnerService.show()
    this.clientService.Details(this.clientId).subscribe({
      next: (client) => {
        this.clientDetails = client
        this.age = client.age
        this.dob = client.dateOfBirth
        this.setClientInformation(client)
        this._hasAccess.flipHasAccess =
          this.commonService.clientHasAccessToFlip(client)
        this._hasAccess.reddiHasAccess =
          this.commonService.clientHasAccessToReddi(client)
        this._hasAccess.slatHasAccess =
          this.commonService.clientHasAccessToSlat(client)
        this._totalReviews = this.commonService.returnReviewIndicator(client)
        this.loadNdisNumber(client)
        this.recomputeVisibleSteps()
        this.announceCurrentStep()
        // Rail re-renders live as the plan-type step's answers change (AC10) — not
        // only when Continue is clicked.
        this.payloadSub = this.state.payload$.subscribe(() => {
          this.recomputeVisibleSteps()
          this.cd.markForCheck()
        })
        this.cd.markForCheck()
      },
      error: () => {
        this.spinnerService.hide()
        this.bannerService.showBanner(
          'An error occurred while retrieving the client record.',
          'error',
          30000,
        )
      },
      complete: () => {
        this.spinnerService.hide()
      },
    })
  }

  private setClientInformation(client: ClientDto): void {
    this.commonService.setAttribute(
      this.clientInformationAttributes,
      'Age',
      client.age,
    )
    this.commonService.setAttribute(
      this.clientInformationAttributes,
      'DOB',
      moment(client.dateOfBirth).format('Do MMMM, YYYY'),
    )
    this.commonService.setAttribute(
      this.clientInformationAttributes,
      'Gender',
      client.genderCodeName,
    )
    this.commonService.setAttribute(
      this.clientInformationAttributes,
      'Case Manager',
      client.clientCaseManagerName,
    )
    // AC9 — a 5th attribute, not part of CommonService.createEmptyAttributeList() (shared by
    // every other client-scoped screen, which has no business showing this). Pushed blank here;
    // loadNdisNumber() fills it in once the separate NDIS-number lookup resolves.
    this.clientInformationAttributes.push(
      this.commonService.createAttribute(this.ndisNumberAttributeLabel, '', ''),
    )
  }

  // AC12 — every new user-visible string uses ngx-translate, including this one. Resolved via
  // TranslateService (matching PlanTypeStepComponent's/NdisPlanStepComponent's own label pattern)
  // rather than a template pipe, since ClientInformationEntryComponent's [key] input renders
  // attribute.display as plain text with no translate pipe of its own.
  private get ndisNumberAttributeLabel(): string {
    return this.translate.instant('SERVICE_PLAN.CLIENT_INFO.NDIS_NUMBER_LABEL')
  }

  // Story e8-2: resolved via a separate call (NdisPlanService), not ClientDto/GetClientDetails —
  // see NdisPlanService's own doc comment for why this isn't folded onto the shared client-details
  // endpoint every client-scoped screen calls. Best-effort only: Step 2 has no required fields
  // (AC3), so a failed/slow NDIS-number lookup must not block or alarm the clinician.
  private loadNdisNumber(client: ClientDto): void {
    this.ndisNumberSub = this.ndisPlanService
      .GetNdisNumber(this.clientId)
      .subscribe({
        next: (result) => {
          client.ndisNumber = result.ndisNumber
          this.commonService.setAttribute(
            this.clientInformationAttributes,
            this.ndisNumberAttributeLabel,
            result.ndisNumber ?? '',
          )
          this.cd.markForCheck()
        },
        error: () => {
          // Degrade silently — the field simply stays unprefilled/blank.
        },
      })
  }

  private goToStep(stepId: ServicePlanStepId): void {
    this.currentStepId = stepId
    this.state.setCurrentStepId(stepId)
    this.recomputeVisibleSteps()
    this.announceCurrentStep()
    this.cd.markForCheck()
    clearTimeout(this.focusTimeoutId)
    this.focusTimeoutId = setTimeout(() => {
      this.stepHeading?.nativeElement.focus()
      // Second change-detection pass, scheduled for the next macrotask so it runs
      // after `#activeStep`'s dynamic ViewChild query (behind the *ngIf/else chain)
      // has settled. That query only refreshes to point at the just-mounted step
      // AFTER this component's own template bindings are evaluated in the SAME
      // pass that creates it — so isContinueDisabled's read of `this.activeStep`
      // above can still see the previous step's reference while the new step is
      // being created. Symptom without this: Continue's disabled state lags one
      // step-change behind, most noticeably when returning to an already-valid
      // required step (e.g. Step 4 once already confirmed) — it renders disabled
      // until some other click forces another, now-correct, CD pass.
      this.cd.markForCheck()
    })
  }

  private recomputeVisibleSteps(): void {
    const previousStepIds = this.visibleSteps.map((step) => step.id)
    const payload = this.state.getPayload()
    this.visibleSteps = payload.planType
      ? getVisibleSteps(payload.planType, payload.ndis.hasNdis ?? false).map(
          (id) => getStepDefinition(id),
        )
      : [getStepDefinition('planType')]

    // The visible-step list can change shape (plan type/hasNdis answered
    // differently) after the clinician has already advanced past it — e.g.
    // jumping back to Step 1 via the rail and picking a different plan type.
    // maxReachedIndex was computed against the OLD list's indices, so once the
    // list's shape actually changes it must be clamped to the current step's
    // position in the NEW list, rather than left pointing at whatever step now
    // happens to sit at that old index (which may never have been validated
    // under the new answer). A plain rail jump back to an already-reached step,
    // with no answer actually changing, must NOT reset progress — so only clamp
    // when the step-id sequence itself differs from before.
    const visibleStepsChanged =
      previousStepIds.length !== this.visibleSteps.length ||
      previousStepIds.some((id, i) => id !== this.visibleSteps[i].id)

    if (visibleStepsChanged) {
      const currentIndex = this.visibleSteps.findIndex(
        (step) => step.id === this.currentStepId,
      )
      if (currentIndex >= 0) {
        this.maxReachedIndex = Math.min(this.maxReachedIndex, currentIndex)
      }
    }
  }

  private announceCurrentStep(): void {
    const index = this.visibleSteps.findIndex(
      (step) => step.id === this.currentStepId,
    )
    const definition = getStepDefinition(this.currentStepId)
    this.announcer.announceStep(
      index + 1,
      this.visibleSteps.length,
      definition.titleKey,
    )
  }

  private async showAreYouSureDialog(): Promise<boolean> {
    const dialogRef = this.dialog.open(AreYouSureModalComponent, {
      width: '605px',
      height: '316px',
      panelClass: 'are-you-sure-panel',
      // Leaving-with-unsaved-answers must be an explicit choice — without this,
      // Escape/backdrop-click dismiss the dialog and resolve afterClosed() with
      // `undefined` instead of either of the modal's own true/false outcomes.
      disableClose: true,
    })
    const result = await firstValueFrom(dialogRef.afterClosed())
    return result === true
  }
}
